import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { api } from "@/lib/api";
import { TOOLS, ICONS, CUSTOM_ICONS, CATEGORIES, toolsForCategory } from "@/lib/toolRegistry";
import { VerifyEmailBanner } from "@/pages/VerifyEmail";
import WelcomeSetup from "@/components/WelcomeSetup";
import { ArrowUpRight, Lock, CreditCard, FolderArchive, Clock, Megaphone as MegaphoneIcon, ArrowRight, Compass, Zap, Search as SearchIcon, Sparkles } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const { sub, canUse, badgeForTier } = useSubscription();
  const [recent, setRecent] = useState([]);
  const [toolConfig, setToolConfig] = useState({});
  const [strategyAccess, setStrategyAccess] = useState(null);
  const [strategyStatus, setStrategyStatus] = useState(null);
  const [activeCat, setActiveCat] = useState("social");
  const [toolQuery, setToolQuery] = useState("");
  const firstName = (user?.name || "Diver").split(" ")[0];

  useEffect(() => {
    api.get("/vault/recent?limit=6").then(({ data }) => setRecent(data.items || [])).catch(() => {});
    api.get("/tools/config").then(({ data }) => {
      const map = {};
      (data.items || []).forEach((c) => { map[c.tool] = c; });
      setToolConfig(map);
    }).catch(() => {});
    api.get("/strategy/access").then(({ data }) => setStrategyAccess(data)).catch(() => {});
    api.get("/strategy/foundation").then(({ data }) => setStrategyStatus(data)).catch(() => { setStrategyStatus({ approval_status: "not_started" }); });
  }, [sub?.tier, sub?.status]);

  const archived = sub?.status === "archived";
  const trialBanner = sub?.status === "trialing"
    ? `You're on the 7-day ${badgeForTier ? badgeForTier(sub.tier) : "Pro"} trial. ${sub.trial_days_left} day${sub.trial_days_left === 1 ? "" : "s"} left.`
    : null;
  const lowCreditsBanner = sub && !sub.is_unlimited_credits && sub.credits_remaining != null && sub.credits_remaining <= 5 && sub.credits_remaining > 0 && !archived
    ? `Only ${sub.credits_remaining} Strategy Credit${sub.credits_remaining === 1 ? "" : "s"} left this period.`
    : null;
  const archivedBanner = archived
    ? "Your Dive Brain has been created and safely stored. Upgrade your account to continue using your personalised marketing system."
    : null;

  const renderToolCard = (t, i) => {
    const Icon = ICONS[t.icon];
    const customIcon = CUSTOM_ICONS[t.icon];
    const conf = toolConfig[t.backendKey];
    const cost = conf?.credit_cost ?? null;
    const locked = conf ? !conf.allowed : !canUse(t.tier);
    const reason = conf?.reason;
    const tierLabel = t.tier === "pro_unlimited" ? "Pro Unlimited" : t.tier === "pro" ? "Pro" : "Creator";
    const gateLabel = reason === "trial_gated" ? "Upgrade To Unlock"
      : reason === "wrong_tier" ? tierLabel
      : reason === "out_of_credits" ? "Out of credits"
      : reason === "tool_disabled" ? "Disabled"
      : tierLabel;
    return (
      <Link
        key={t.key}
        to={locked ? "/billing" : t.route}
        data-testid={`card-${t.key}`}
        data-locked={locked ? "true" : "false"}
        data-tool-reason={reason || "ok"}
        className={`dms-card dms-card-hover p-5 sm:p-6 group animate-fade-up ${locked ? "opacity-70" : ""} ${t.premium ? "border-[#4FD1C5]/50" : ""}`}
        style={{ animationDelay: `${i * 30}ms` }}
      >
        <div className="flex items-start justify-between">
          <div className={`w-10 h-10 sm:w-11 sm:h-11 rounded-md flex items-center justify-center overflow-hidden ${locked ? "bg-[#F1F5F9] text-[#94A3B8]" : t.premium ? "bg-[#0A2342] text-[#4FD1C5]" : t.tier === "pro" ? "bg-[#0A2342]/5 text-[#0A2342]" : "bg-[#4FD1C5]/15 text-[#1D4E89]"}`}>
            {customIcon ? (
              <img src={customIcon} alt="" className={`w-6 h-6 sm:w-7 sm:h-7 object-contain ${locked ? "opacity-60 grayscale" : ""}`} />
            ) : (
              Icon && <Icon className="w-5 h-5" strokeWidth={1.75} />
            )}
          </div>
          <div className="flex flex-col items-end gap-1">
            {locked ? (
              <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase font-bold text-[#94A3B8]"><Lock className="w-3 h-3" /> {gateLabel}</span>
            ) : t.premium ? (
              <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase font-bold text-[#0A2342]"><Sparkles className="w-3 h-3 text-[#F59E0B]" /> Premium</span>
            ) : t.tier === "pro" ? (
              <span className="text-[10px] tracking-[0.18em] uppercase font-bold text-[#1D4E89]">Pro</span>
            ) : (
              <span className="text-[10px] tracking-[0.18em] uppercase font-bold text-[#0F766E]">Creator</span>
            )}
            {t.unlimited ? (
              <span data-testid={`card-${t.key}-cost`} className="text-[10.5px] font-bold text-[#0F766E]">Unlimited</span>
            ) : cost != null ? (
              <span data-testid={`card-${t.key}-cost`} className="text-[10.5px] font-bold text-[#64748B]">{cost} credit{cost === 1 ? "" : "s"}</span>
            ) : null}
          </div>
        </div>
        <h3 className="mt-4 font-heading font-bold text-[15px] sm:text-[16px] text-[#0A2342] leading-snug">{t.title}</h3>
        <p className="text-[13px] sm:text-[13.5px] text-[#64748B] mt-1.5 leading-relaxed">{t.short}</p>
      </Link>
    );
  };

  return (
    <div data-testid="dashboard-page" className="px-5 sm:px-6 lg:px-10 py-8 sm:py-10 lg:py-14 max-w-7xl">
      <div className="dms-label mb-3">Welcome back</div>
      <h1 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-[#0A2342]">Good to see you, {firstName}.</h1>
      <p className="mt-2 sm:mt-3 text-[#475569] max-w-2xl text-[14.5px] sm:text-base">Pick a tool. Every output is engineered against your Dive Brain and the DIL doctrine, then auto-saved to your Content Vault.</p>

      {/* Welcome + setup checklist. Auto-hides once all three steps are done. */}
      {!archived && <WelcomeSetup firstName={firstName} />}

      {/* The old operator-setup-banner has been folded into WelcomeSetup's
          step 3 above. Once the user completes all three setup steps the
          WelcomeSetup card disappears and this dashboard returns to a clean
          tool-first layout. */}
      {false && strategyAccess?.has_access && strategyStatus && !["strategy_approved"].includes(strategyStatus.approval_status) && !archived && (
        <Link
          to={strategyStatus.approval_status === "not_started" ? "/strategy/onboarding" : "/strategy"}
          data-testid="operator-setup-banner"
          className="mt-6 sm:mt-8 block group relative overflow-hidden rounded-xl p-5 sm:p-6 bg-[#0A2342] text-white hover:shadow-xl transition-all"
        >
          <div className="absolute -right-8 -top-8 w-48 h-48 rounded-full bg-[#4FD1C5]/15 group-hover:bg-[#4FD1C5]/25 transition-colors" />
          <div className="absolute -right-24 -bottom-24 w-64 h-64 rounded-full bg-[#4FD1C5]/5" />
          <div className="relative flex items-start gap-4">
            <div className="w-11 h-11 rounded-lg bg-[#4FD1C5] text-[#0A2342] flex items-center justify-center shrink-0">
              <Zap className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <div className="text-[10.5px] tracking-[0.22em] uppercase font-bold text-[#4FD1C5]">
                {strategyStatus.approval_status === "not_started" ? "Recommended next step" : "Pick up where you left off"}
              </div>
              <h2 className="font-heading font-bold text-lg sm:text-xl mt-1 leading-tight">
                {strategyStatus.approval_status === "not_started"
                  ? "Set up your 90-day Operator to unlock daily posting prompts."
                  : "Finish your 90-day Operator — you're one step away from daily prompts."}
              </h2>
              <p className="text-[13.5px] text-white/80 mt-2 max-w-2xl">
                Your team can execute daily marketing without you touching a keyboard. Every morning, the Operator sends ready-to-post content based on the strategy you approve — you stay in control, they stay busy.
              </p>
              <div className="mt-4 inline-flex items-center gap-2 rounded-md bg-[#4FD1C5] text-[#0A2342] px-4 py-2 text-[13px] font-semibold group-hover:gap-3 transition-all">
                {strategyStatus.approval_status === "not_started" ? "Start setup" : "Continue setup"} <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          </div>
        </Link>
      )}

      {(trialBanner || lowCreditsBanner || archivedBanner) && (
        <div
          data-testid="plan-banner"
          className={`mt-5 sm:mt-6 dms-card p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 ${archivedBanner ? "border-[#FCD34D] bg-[#FEFCE8]" : ""}`}
        >
          <div className="flex items-start sm:items-center gap-3">
            <CreditCard className="w-5 h-5 text-[#1D4E89] shrink-0 mt-0.5 sm:mt-0" />
            <div className="text-[13.5px] sm:text-[14.5px] text-[#1F2937]">{archivedBanner || trialBanner || lowCreditsBanner}</div>
          </div>
          <Link to="/billing" data-testid="plan-banner-cta" className="dms-btn-accent w-full sm:w-auto justify-center">Choose plan</Link>
        </div>
      )}

      <div className="mt-5"><VerifyEmailBanner /></div>

      {/* Recent Assets */}
      {recent.length > 0 && (
        <section data-testid="recent-assets" className="mt-10 sm:mt-12">
          <div className="flex items-end justify-between mb-4">
            <div>
              <div className="dms-label mb-2">Recent</div>
              <h2 className="text-xl sm:text-2xl font-heading font-bold text-[#0A2342]">Pick up where you left off</h2>
            </div>
            <Link to="/vault" className="text-[13px] font-semibold text-[#1D4E89] hover:text-[#0A2342]">View all →</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {recent.map((r) => (
              <Link key={r.id} to={`/vault/${r.id}`} data-testid={`recent-${r.id}`} className="dms-card dms-card-hover p-4 sm:p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-3 h-3 text-[#64748B]" />
                  <span className="text-[11px] tracking-[0.18em] uppercase font-bold text-[#64748B]">{r.category}</span>
                </div>
                <div className="font-heading font-bold text-[14.5px] text-[#0A2342] line-clamp-2 leading-snug">{r.title}</div>
                <div className="mt-2 text-[12px] text-[#94A3B8]">{new Date(r.created_at).toLocaleDateString()}</div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="mt-10 sm:mt-12" data-testid="dashboard-strategy-cta">
        {strategyAccess?.has_access ? (
          <Link to="/strategy" className="block dms-card p-6 md:p-8 relative overflow-hidden hover:-translate-y-0.5 hover:shadow-lg transition-all group border-[#4FD1C5]/40">
            <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-[#4FD1C5]/10 group-hover:bg-[#4FD1C5]/20 transition-colors" />
            <div className="relative flex items-start gap-4 md:gap-6">
              <div className="w-12 h-12 rounded-lg bg-[#0A2342] text-[#4FD1C5] flex items-center justify-center shrink-0">
                <Compass className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <div className="text-[10.5px] tracking-[0.22em] uppercase font-bold text-[#4FD1C5]">Premium · 90-Day Strategy</div>
                <h2 className="font-heading font-bold text-xl sm:text-2xl text-[#0A2342] leading-tight mt-1">Open your Strategy Operator</h2>
                <p className="text-[13.5px] text-[#475569] mt-2 max-w-xl">
                  Your rolling 90-day plan, weekly content prep and daily reminders — all in one place.
                </p>
              </div>
              <div className="hidden sm:inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#0A2342] shrink-0 mt-2 group-hover:gap-2.5 transition-all">
                Open <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          </Link>
        ) : strategyAccess ? (
          <Link to="/strategy" className="block dms-card p-6 md:p-8 relative overflow-hidden hover:-translate-y-0.5 hover:shadow-lg transition-all group border-[#0A2342]/10" data-testid="dashboard-strategy-upsell">
            <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-[#0A2342]/5" />
            <div className="relative flex items-start gap-4 md:gap-6">
              <div className="w-12 h-12 rounded-lg bg-[#0A2342] text-[#4FD1C5] flex items-center justify-center shrink-0">
                <Compass className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <div className="text-[10.5px] tracking-[0.22em] uppercase font-bold text-[#4FD1C5] flex items-center gap-1.5"><Lock className="w-3 h-3" /> Premium feature</div>
                <h2 className="font-heading font-bold text-xl sm:text-2xl text-[#0A2342] leading-tight mt-1">Stop creating random content.</h2>
                <p className="text-[13.5px] text-[#475569] mt-2 max-w-xl">
                  Build your 90-day dive marketing strategy, prepare content weekly, and get daily reminders telling you exactly what to post, where, and why.
                </p>
                <div className="mt-3 inline-flex items-center gap-2 rounded-md bg-[#0A2342] text-white px-4 py-2 text-[13px] font-semibold">
                  Build My 90-Day Strategy <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </div>
          </Link>
        ) : null}
      </section>

      <section className="mt-10 sm:mt-12" data-testid="dashboard-campaigns-cta">
        <Link to="/campaigns" className="block dms-card p-6 md:p-8 relative overflow-hidden hover:-translate-y-0.5 hover:shadow-lg transition-all group border-[#0A2342]/10">
          <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-[#4FD1C5]/10 group-hover:bg-[#4FD1C5]/20 transition-colors" />
          <div className="absolute -right-16 -bottom-16 w-48 h-48 rounded-full bg-[#0A2342]/5" />
          <div className="relative flex items-start gap-4 md:gap-6">
            <div className="w-12 h-12 rounded-lg bg-[#0A2342] text-[#4FD1C5] flex items-center justify-center shrink-0">
              <MegaphoneIcon className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <div className="text-[10.5px] tracking-[0.22em] uppercase font-bold text-[#4FD1C5]">Campaign Builder</div>
              <h2 className="font-heading font-bold text-xl sm:text-2xl text-[#0A2342] leading-tight mt-1">Plan your next campaign</h2>
              <p className="text-[13.5px] text-[#475569] mt-2 max-w-xl">Pick a goal (Reef Awareness Week, Open Water Launch, Black Friday…), pick a duration, and we design one coordinated narrative across every piece.</p>
            </div>
            <div className="hidden sm:inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#0A2342] shrink-0 mt-2 group-hover:gap-2.5 transition-all">
              Open <ArrowRight className="w-4 h-4" />
            </div>
          </div>
        </Link>
      </section>

      <section className="mt-10 sm:mt-12" data-testid="tool-nav">
        <div className="dms-label mb-2">Marketing Tools</div>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <h2 className="text-xl sm:text-2xl font-heading font-bold text-[#0A2342]">What do you want to do?</h2>
          <div className="relative w-full sm:w-72">
            <SearchIcon className="w-4 h-4 text-[#94A3B8] absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="tool-search"
              value={toolQuery}
              onChange={(e) => setToolQuery(e.target.value)}
              placeholder="Search all tools…"
              className="w-full pl-9 pr-3 py-2.5 rounded-md border border-[#E2E8F0] bg-white text-[14px] text-[#0A2342] focus:outline-none focus:border-[#4FD1C5]"
            />
          </div>
        </div>

        {toolQuery.trim() ? (
          // Flat search results across every category.
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4" data-testid="tool-search-results">
            {TOOLS.filter((t) =>
              (t.title + " " + t.short).toLowerCase().includes(toolQuery.trim().toLowerCase())
            ).map((t, i) => renderToolCard(t, i))}
            {TOOLS.filter((t) => (t.title + " " + t.short).toLowerCase().includes(toolQuery.trim().toLowerCase())).length === 0 && (
              <div className="col-span-full text-[14px] text-[#94A3B8]">No tools match &ldquo;{toolQuery}&rdquo;.</div>
            )}
          </div>
        ) : (
          <>
            {/* Category cards */}
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4" data-testid="category-cards">
              {CATEGORIES.map((c) => {
                const CatIcon = c.icon;
                const isActive = activeCat === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    data-testid={`category-${c.id}`}
                    onClick={() => setActiveCat(c.id)}
                    className={`text-left dms-card p-5 transition-all ${isActive ? "ring-2 ring-[#4FD1C5] border-[#4FD1C5]" : "dms-card-hover"}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className={`w-11 h-11 rounded-md flex items-center justify-center ${isActive ? "bg-[#0A2342] text-[#4FD1C5]" : "bg-[#4FD1C5]/15 text-[#1D4E89]"}`}>
                        <CatIcon className="w-5 h-5" strokeWidth={1.75} />
                      </div>
                      <span className="text-[11px] font-bold text-[#94A3B8]">{c.toolKeys.length} tools</span>
                    </div>
                    <h3 className="mt-4 font-heading font-bold text-[15px] sm:text-[16px] text-[#0A2342] leading-snug">{c.name}</h3>
                    <p className="text-[13px] text-[#64748B] mt-1.5 leading-relaxed">{c.blurb}</p>
                  </button>
                );
              })}
            </div>

            {/* Tools inside the active category */}
            <div className="mt-8" data-testid={`category-tools-${activeCat}`}>
              <div className="flex items-center gap-2 mb-4 text-[13px] text-[#64748B]">
                <span className="font-semibold text-[#0A2342]">
                  {CATEGORIES.find((c) => c.id === activeCat)?.name}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
                {toolsForCategory(activeCat).map((t, i) => renderToolCard(t, i))}
              </div>
            </div>
          </>
        )}

        <div className="mt-8 text-center text-[13px] text-[#94A3B8]" data-testid="tools-coming-soon">
          More dive marketing tools are coming soon. Have a request? Use the <span className="font-semibold text-[#1D4E89]">Report a bug</span> button and pick &ldquo;Feature request&rdquo;.
        </div>
      </section>

      <Link to="/vault" data-testid="vault-shortcut" className="mt-10 sm:mt-12 dms-card dms-card-hover p-5 sm:p-6 flex items-center gap-4">
        <div className="w-11 h-11 rounded-md bg-[#4FD1C5]/15 text-[#1D4E89] flex items-center justify-center"><FolderArchive className="w-5 h-5" /></div>
        <div className="flex-1">
          <div className="font-heading font-bold text-[#0A2342]">Open Content Vault</div>
          <div className="text-sm text-[#64748B]">Every output you&apos;ve generated. Search, edit, export.</div>
        </div>
        <ArrowUpRight className="w-4 h-4 text-[#94A3B8]" />
      </Link>
    </div>
  );
}
